'use client';

import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown';
import { type CSSProperties, type JSX, memo } from 'react';
import remarkGfm from 'remark-gfm';

import '@assistant-ui/react-markdown/styles/dot.css';

/**
 * react-markdown forwards a non-DOM `node` (hast AST) prop to every component
 * override (passNode is on). Strip it so it isn't spread onto a real DOM element,
 * while preserving the remaining attributes (colSpan, rowSpan, className, style, …).
 */
function withoutNode<T extends { node?: unknown }>(props: T): Omit<T, 'node'> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { node, ...rest } = props;
    return rest;
}

/** Merge a base className with an optional incoming one from react-markdown. */
function mergeClassName(base: string, incoming?: string): string {
    return incoming ? `${base} ${incoming}` : base;
}

/**
 * Simple markdown text component for rendering assistant messages.
 * Uses assistant-ui's MarkdownTextPrimitive with custom styling.
 *
 * remark-gfm enables GitHub-Flavored Markdown (tables, strikethrough, etc.);
 * MarkdownTextPrimitive wraps react-markdown, which is CommonMark-only without it,
 * so markdown tables would otherwise render as plaintext.
 */
function MarkdownTextImpl(): JSX.Element {
    return (
        <MarkdownTextPrimitive
            remarkPlugins={[remarkGfm]}
            className="prose prose-sm max-w-none prose-p:my-2 prose-p:leading-relaxed prose-strong:font-semibold prose-ul:my-2 prose-ol:my-2"
            components={{
                p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                ul: ({ children }) => <ul className="my-2 ml-4 list-disc space-y-1">{children}</ul>,
                ol: ({ children }) => <ol className="my-2 ml-4 list-decimal space-y-1">{children}</ol>,
                li: ({ children }) => <li className="text-sm">{children}</li>,
                a: ({ href, children }) => (
                    <a
                        href={href}
                        className="text-blue-600 hover:text-blue-800 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {children}
                    </a>
                ),
                // Table overrides forward GFM-derived props: `align` (from :---: syntax)
                // is applied via style.textAlign, and colSpan/rowSpan/className/style pass
                // through so the parsed table semantics are preserved.
                table: ({ children, className, ...props }) => (
                    <div className="my-3 overflow-x-auto">
                        <table
                            className={mergeClassName('w-full border-collapse text-sm', className)}
                            {...withoutNode(props)}
                        >
                            {children}
                        </table>
                    </div>
                ),
                th: ({ children, align, className, style, ...props }) => (
                    <th
                        className={mergeClassName(
                            'border border-gray-200 bg-gray-50 px-3 py-1.5 font-semibold',
                            className,
                        )}
                        style={{ textAlign: (align as CSSProperties['textAlign']) ?? 'left', ...style }}
                        {...withoutNode(props)}
                    >
                        {children}
                    </th>
                ),
                td: ({ children, align, className, style, ...props }) => (
                    <td
                        className={mergeClassName('border border-gray-200 px-3 py-1.5 align-top', className)}
                        style={{ textAlign: align as CSSProperties['textAlign'], ...style }}
                        {...withoutNode(props)}
                    >
                        {children}
                    </td>
                ),
            }}
        />
    );
}

export const MarkdownText = memo(MarkdownTextImpl);
